import React, { useState } from "react";
import dayjs from "dayjs";
import {
  OverviewContainer,
  BasicInformation,
  BasicInformationContent,
  BasicInformationHeader,
  LoginInformation,
  LoginInformationContent,
  LoginInformationContentCardWrapper,
  LoginInformationContentCardLeft,
  LoginInformationContentCardRight,
  MultiFactor,
  MultiFactorHeader,
  MultiFactorState,
  MultiFactorStateLeft,
  MultiFactorStateRight,
  InfoItemRow
} from "./Overview.styled";
import { Typography, Button, Icon, Card, IconHighlight, Avatar} from "@ann/web-components";
import { EditRoleModal } from "../../components";

// Format creation date: "Oct 31 · 2025"
const formatCreationDate = (date) => {
  if (!date) return null;
  try {
    return dayjs(date).format("MMM D · YYYY");
  } catch (error) {
    console.error("Error formatting creation date:", error);
    return null;
  }
};

// Format last update: "Nov 2 · 2025 · 10:00 AM"
const formatLastUpdate = (date) => {
  if (!date) return null;
  try {
    return dayjs(date).format("MMM D · YYYY · h:mm A");
  } catch (error) {
    console.error("Error formatting last update:", error);
    return null;
  }
};

const Overview = ({ roleId, roleName, roleDescription, roleData, onRoleUpdate }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Use roleData if available, otherwise fallback to individual props
  const name = roleData?.name || roleName || "Role Name";
  const description = roleData?.description || roleDescription || "Role Description";

  return (
    <OverviewContainer>
      <BasicInformation id="role-information">
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Role Information
          </Typography>

          <Button 
            type="secondary" 
            label="Edit role" 
            size="small" 
            disableEffects="true" 
            iconLeft="edit"
            onClick={() => setIsEditModalOpen(true)}
          />

        </BasicInformationHeader>

        <Card size="medium">
          <BasicInformationContent>
            <div>
              <Typography category="body" size="medium" weight="regular">
                Role Name
              </Typography>
              <Typography category="body" size="medium" weight="regular" color="body">
                {name}
              </Typography>
            </div>
            <div>
              <Typography category="body" size="medium" weight="regular">
                Role Description
              </Typography>
              <Typography category="body" size="medium" weight="regular" color="body">
                {description}
              </Typography>
            </div>
      
          </BasicInformationContent>
        </Card>
      </BasicInformation>

      <LoginInformation>
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Role Statistics
          </Typography>

        </BasicInformationHeader>

        <LoginInformationContent>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Template used
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  Sales
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Primary" icon="copy" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
              <Typography category="body" size="medium" weight="regular">
                  Modules with permissions
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  7 modules
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Alert" icon="lock_open" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Total permissions
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  32
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Neutral" icon="list" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>

          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="medium" weight="regular">
                  Creation date
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  {roleData?.created_at ? formatCreationDate(roleData.created_at) : "Oct 31 · 2025"}
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Success" icon="calendarToday" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>


            </LoginInformationContentCardWrapper>
          </Card>
          

        </LoginInformationContent>
      </LoginInformation>

      <MultiFactor>
        <MultiFactorHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Log information
          </Typography>
        </MultiFactorHeader>

        <MultiFactorState>
          <MultiFactorStateLeft>
            <Typography category="body" size="medium" weight="regular">
              Created by
            </Typography>
      
            <InfoItemRow>
              <Avatar alt="Default avatar" size="sm" src="https://placehold.co/16x16"/>
              <Typography category="body" size="medium" weight="regular" color="body">
                Emma Wilson ·  Sales Manager
              </Typography>
            </InfoItemRow>
          </MultiFactorStateLeft>

          <MultiFactorStateRight> 
            <Typography category="body" size="medium" weight="regular">
             Last Update
            </Typography>
            <InfoItemRow>
              <Icon name="schedule" size="small" />
              <Typography category="body" size="medium" weight="regular" color="body">
                Last update: {roleData?.updated_at ? formatLastUpdate(roleData.updated_at) : "Nov 2 · 2025 · 10:00 AM"}
              </Typography>
            </InfoItemRow>
          </MultiFactorStateRight>
        </MultiFactorState>
      </MultiFactor>

      <EditRoleModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)}
        roleUuid={roleId}
        roleName={name}
        roleDescription={description}
        onSave={(result) => {
          if (result.success) {
            // Update parent state (Roles.jsx) to refresh Header and Breadcrumb
            onRoleUpdate?.(result.data);
          }
        }}
      />
    </OverviewContainer>
  );
};

export default Overview;

